import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { OnboardingContext } from '../InvoiceOnboarding';
import MainContent from '../../../../generic-components/Layout/MainContent';
import { ACTUAL_FLOW_STEPS, STYLES, SUBMIT_TYPES, TEXTS } from '../consts/consts';
import Button from '../../../../generic-components/Button';
import LinedStepper from '../../../../generic-components/LinedStepper';
import { LogoList } from './LogoList';
import DragAndDropArea from './DragAndDropArea';
import { useUploadCustomLogoMutation } from '../../../../hooks/mutations/useUploadCustomLogoMutation';
import { useStore } from '../../../../store';
import { InvoicesContext } from '../../contexts/InvoicesContext';
import { UseGetInvoiceCustomLogo } from '../../../../hooks/query/useGetInvoiceCustomLogo';
import Icon from '../../../../generic-components/Icon';
import { ReactComponent as TrashBin } from '../../../../assets/icons/svg/shared/ic-shared-trash.svg';

const StepLogo = () => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const invoiceContext = useContext(InvoicesContext);
  const flowContext = useContext(OnboardingContext);
  const { onStepSubmit, activeStep, customizationData, isLoadingState } = flowContext;
  const {
    data,
    isLoading: isGetCustomLogoUrl,
    isRefetching,
  } = UseGetInvoiceCustomLogo({ url: invoiceContext?.settings?.logoFileUrl || '' });
  const [selectedLogos, setSelectedLogos] = useState<string[]>([]);
  const [customLogo, setCustomLogo] = useState<File | null>(null);
  const [isCustomLogoMode, setIsCustomLogoMode] = useState<boolean>(false);
  const [shouldTriggerLogoSelect, setShouldTriggerLogoSelect] = useState<boolean>(false);
  const [customLogoUrl, setCustomLogoUrl] = useState<string>('');
  const isEditMode = !!invoiceContext.initialObStep;

  const {
    mutate: uploadLogo,
    isLoading,
    data: uploadResponse,
  } = useUploadCustomLogoMutation({
    customerId,
    logo: customLogo,
    onSuccess: (logoData) => {
      onStepSubmit({ type: SUBMIT_TYPES.LOGO.LOGO_FILE_ID, data: logoData });
    },
    onError: () => {
      setCustomLogo(null);
      toast.error('Something went wrong. Please try again', {
        position: 'bottom-center',
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    },
  });

  const getPreSelectedLogoId = () => {
    if (!isEditMode && flowContext?.invoiceCustomData?.liliLogoId) {
      return flowContext?.invoiceCustomData?.liliLogoId;
    }
    if (invoiceContext?.settings?.liliLogoId) {
      return invoiceContext?.settings?.liliLogoId;
    }
    return '';
  };

  const handleLogosSelection = (logoList: string[]) => {
    if (isCustomLogoMode && logoList.length > 0) setIsCustomLogoMode(false);
    setSelectedLogos(logoList);
    onStepSubmit({ type: SUBMIT_TYPES.LOGO.LILI_LOGO_ID, data: logoList });
  };

  const handleSetCustomLogo = (file: File | null) => {
    if (!isCustomLogoMode && file) setIsCustomLogoMode(true);
    setCustomLogo(file);
  };

  const isLocalLoading = useMemo(
    () => isLoading || isGetCustomLogoUrl || isRefetching || isLoadingState,
    [isLoading, isGetCustomLogoUrl, isRefetching, isLoadingState],
  );

  useEffect(() => {
    if (isCustomLogoMode && customLogo) {
      onStepSubmit({ type: SUBMIT_TYPES.LOGO.LOGO_FILE_ID, data: uploadResponse });
    }
  }, [selectedLogos, customLogo]);

  useEffect(() => {
    if (customLogo) {
      uploadLogo();
    } else {
      setIsCustomLogoMode(false);
    }
  }, [customLogo]);

  useEffect(() => {
    if (data) {
      setCustomLogoUrl(data);
      setIsCustomLogoMode(true);
      if (invoiceContext?.settings?.logoFileUrl) {
        onStepSubmit({
          type: SUBMIT_TYPES.LOGO.LOGO_FILE_ID,
          data: {
            url: invoiceContext?.settings?.logoFileUrl,
            externalId: invoiceContext?.settings?.logoFileId,
          },
        });
      }
    }
  }, [data]);

  return customizationData?.liliLogos ? (
    <MainContent className={STYLES.PAGES.LOGO.MAIN_CONTENT}>
      {!isEditMode && (
        <LinedStepper
          currentStep={activeStep}
          mainContainerCSS={STYLES.LINED_STEPPER.MAIN_CONTAINER}
          passStepAnimation={STYLES.LINED_STEPPER.STEP_ANIMATION}
          stepCSS={STYLES.LINED_STEPPER.STEP}
          stepContainerCSS={STYLES.LINED_STEPPER.STEP_CONTAINER}
          stepsAmount={ACTUAL_FLOW_STEPS}
        />
      )}
      <span className={STYLES.PAGES.LOGO.TITLE}>{t(TEXTS.STEP_LOGO.TITLE)}</span>
      <LogoList
        triggerInitialSelect={shouldTriggerLogoSelect}
        preSelectLogoId={getPreSelectedLogoId()}
        isCustomLogoSet={customLogo !== null}
        isCustomLogoMode={isCustomLogoMode}
        maxSelection={1}
        items={customizationData.liliLogos}
        selectedItems={selectedLogos}
        setSelectedItems={handleLogosSelection}
      />
      {isLocalLoading && <Skeleton width='528px' height='210px' />}
      {!customLogoUrl && !isLocalLoading && (
        <DragAndDropArea
          isAreaDisabled={!isCustomLogoMode}
          isLoading={isLocalLoading}
          customLogo={customLogo}
          handleOnClick={() =>
            onStepSubmit({
              type: SUBMIT_TYPES.LOGO.LOGO_FILE_ID,
              data: uploadResponse,
            })}
          setCustomLogo={handleSetCustomLogo}
        />
      )}
      {customLogoUrl && !isLocalLoading && (
        <div className='w-[528px] h-[211px] border-action03 border-[2px] border-solid cursor-default rounded-lg relative'>
          <Icon className='w-full h-full object-contain' src={customLogoUrl} />
          <Button
            onClick={() => {
              setCustomLogoUrl('');
              setIsCustomLogoMode(false);
              setShouldTriggerLogoSelect(true);
              setTimeout(() => {
                setShouldTriggerLogoSelect(false);
              }, 500);
            }}
            className='absolute right-[10px] top-[10px] w-[103px] h-[32px] shadow-lg bg-bw00 border border-bw03 rounded-[100px] flex items-center justify-center text-bw07 text-sm font-metric gap-1 hover:bg-bw02 hover:shadow-none'
            childElement={<TrashBin className='w-[18px] h-[18px] text bw07' />}
            title='Remove'
            id='removeImageButton'
          />
        </div>
      )}
      <Button
        isLoading={isLocalLoading}
        onClick={() => onStepSubmit(selectedLogos)}
        title={invoiceContext.initialObStep ? t('ACTION_TYPE_UPDATE') : t(TEXTS.STEP_LOGO.CTA_NEXT)}
        className={STYLES.PAGES.LOGO.CTA}
        id='elCtaContinue'
      />
    </MainContent>
  ) : (
    <div>Fallback</div>
  );
};

export default StepLogo;
